import { useState } from 'react';
import { 
    Download, Calendar, DollarSign, 
    Loader2, ArrowLeft, UserCheck, Percent,
    ShoppingCart, Star, ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useGetBusinessReport, useGetStaffReport } from '../../hooks/queries/useReports';


const AdminReports = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'business' | 'staff'>('business');
    const [timeframe, setTimeframe] = useState('30d');

    const { data: bizData, isLoading: isBizLoading } = useGetBusinessReport(timeframe);
    const { data: staffData, isLoading: isStaffLoading } = useGetStaffReport(timeframe);

    const business = bizData?.data;
    const staff = staffData?.data?.staff || [];

    const timeframes = [
        { id: '7d', label: 'Last 7 Days' },
        { id: '30d', label: 'Last 30 Days' },
        { id: '90d', label: 'Last Quarter' },
        { id: '12m', label: 'Full Year' },
    ];

    const handleExportCSV = () => {
        // Simple export for current view
        const dataToExport = activeTab === 'business' ? business?.trajectory : staff;
        if (!dataToExport) return;

        let csvContent = "data:text/csv;charset=utf-8,";
        if (activeTab === 'business') {
            if (business) {
            csvContent += "Date,Revenue\n" + business.trajectory.map(t => `${new Date(t.day).toLocaleDateString()},${t.total}`).join("\n");
        }
        } else {
            csvContent += "Staff Name,Role,Total Bookings,Revenue,Cancellations\n" + staff.map(s => `${s.name},${s.role},${s.total_bookings},${s.revenue},${s.cancellations}`).join("\n");
        }
        
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `salon_${activeTab}_report_${timeframe}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const maxRevenue = business?.trajectory ? Math.max(...business.trajectory.map(d => Number(d.total)), 1) : 1;

    return (
        <div className="space-y-8 animate-in fade-in duration-500 pb-20">
            {/* Header section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div>
                    <button 
                        onClick={() => navigate('/admin')}
                        className="flex items-center gap-2 text-xs text-salon-muted hover:text-salon-primary mb-2 transition-colors uppercase tracking-widest font-semibold"
                    >
                        <ArrowLeft className="w-3 h-3" /> Back to Dashboard
                    </button>
                    <h2 className="text-3xl font-serif text-salon-primary">Elite Analytics</h2>
                    <p className="text-sm text-salon-muted mt-1">High-fidelity data insights for Salon Saleh operations.</p>
                </div>
                
                <div className="flex flex-wrap items-center gap-3">
                    {/* Timeframe Selector */}
                    <div className="flex bg-salon-surface/50 border border-salon-golden/10 p-1 rounded-md">
                        {timeframes.map((tf) => (
                            <button
                                key={tf.id}
                                onClick={() => setTimeframe(tf.id)}
                                className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold transition-all rounded ${
                                    timeframe === tf.id 
                                    ? 'bg-salon-golden text-black' 
                                    : 'text-salon-muted hover:text-salon-primary'
                                }`}
                            >
                                {tf.id.toUpperCase()}
                            </button>
                        ))}
                    </div>

                    <button 
                        onClick={handleExportCSV}
                        className="flex items-center gap-2 px-6 py-2.5 bg-salon-golden text-black text-xs font-bold tracking-widest uppercase hover:bg-salon-golden-muted transition-colors rounded shadow-[0_0_15px_rgba(197,160,89,0.2)]"
                    >
                        <Download className="w-4 h-4" /> Export
                    </button>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex border-b border-salon-golden/10 gap-8">
                <button 
                    onClick={() => setActiveTab('business')}
                    className={`pb-4 text-xs uppercase tracking-[0.2em] font-bold transition-all relative ${
                        activeTab === 'business' ? 'text-salon-golden' : 'text-salon-muted hover:text-salon-primary'
                    }`}
                >
                    Business Intelligence
                    {activeTab === 'business' && <motion.div layoutId="tab-active" className="absolute bottom-0 left-0 right-0 h-0.5 bg-salon-golden" />}
                </button>
                <button 
                    onClick={() => setActiveTab('staff')}
                    className={`pb-4 text-xs uppercase tracking-[0.2em] font-bold transition-all relative ${
                        activeTab === 'staff' ? 'text-salon-golden' : 'text-salon-muted hover:text-salon-primary'
                    }`}
                >
                    Staff Performance
                    {activeTab === 'staff' && <motion.div layoutId="tab-active" className="absolute bottom-0 left-0 right-0 h-0.5 bg-salon-golden" />}
                </button>
            </div>

            <AnimatePresence mode="wait">
                {activeTab === 'business' ? (
                    <motion.div 
                        key="business"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="space-y-8"
                    >
                        {isBizLoading ? (
                            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-salon-golden" /></div>
                        ) : (
                            <>
                                {/* Business KPI Cards */}
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                    <div className="bg-salon-surface/50 border border-salon-golden/10 p-6 rounded-lg">
                                        <div className="flex justify-between items-start mb-4">
                                            <span className="text-[10px] uppercase tracking-widest text-salon-muted font-semibold">Net Ecosystem Revenue</span>
                                            <DollarSign className="w-4 h-4 text-salon-golden" />
                                        </div>
                                        <div className="text-3xl font-serif text-salon-primary">${business?.summary.totalRevenue.toLocaleString()}</div>
                                        <div className="text-[10px] text-salon-muted mt-2 flex gap-2">
                                            <span className="text-salon-golden">Bks: ${business?.summary.bookingRevenue.toLocaleString()}</span>
                                            <span className="opacity-30">|</span>
                                            <span>Shop: ${business?.summary.orderRevenue.toLocaleString()}</span>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-salon-surface/50 border border-salon-golden/10 p-6 rounded-lg">
                                        <div className="flex justify-between items-start mb-4">
                                            <span className="text-[10px] uppercase tracking-widest text-salon-muted font-semibold">Waitlist Conversion</span>
                                            <Percent className="w-4 h-4 text-salon-golden" />
                                        </div>
                                        <div className="text-3xl font-serif text-salon-primary">{business?.summary.conversionRate}</div>
                                        <div className="text-[10px] text-salon-muted mt-2">
                                            {business?.summary.waitlistConverted} out of {business?.summary.waitlistTotal} leads converted
                                        </div>
                                    </div>

                                    <div className="bg-salon-surface/50 border border-salon-golden/10 p-6 rounded-lg">
                                        <div className="flex justify-between items-start mb-4">
                                            <span className="text-[10px] uppercase tracking-widest text-salon-muted font-semibold">Active Rituals</span>
                                            <Calendar className="w-4 h-4 text-salon-golden" />
                                        </div>
                                        <div className="text-3xl font-serif text-salon-primary">{business?.summary.waitlistConverted}</div>
                                        <div className="text-[10px] text-salon-muted mt-2">Total bookings in this period</div>
                                    </div>

                                    <div className="bg-salon-surface/50 border border-salon-golden/10 p-6 rounded-lg">
                                        <div className="flex justify-between items-start mb-4">
                                            <span className="text-[10px] uppercase tracking-widest text-salon-muted font-semibold">Inventory Sales</span>
                                            <ShoppingCart className="w-4 h-4 text-salon-golden" />
                                        </div>
                                        <div className="text-3xl font-serif text-salon-primary">${business?.summary.orderRevenue.toLocaleString()}</div>
                                        <div className="text-[10px] text-salon-muted mt-2">Revenue from product orders</div>
                                    </div>
                                </div>

                                {/* Charts & Tables */}
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                    <div className="lg:col-span-2 bg-salon-surface/30 border border-salon-golden/10 rounded-lg p-8">
                                        <h3 className="font-serif text-xl text-salon-primary mb-2">Revenue Growth Path</h3>
                                        <p className="text-xs text-salon-muted mb-12 uppercase tracking-widest">Aggregate growth including bookings and inventory.</p>
                                        
                                        <div className="flex items-end justify-between gap-2 h-72 border-b border-salon-golden/20 pb-4 relative">
                                            {business?.trajectory.map((day, idx) => {
                                                const pct = Math.max((Number(day.total) / maxRevenue) * 100, 5);
                                                return (
                                                    <div key={idx} className="flex-1 relative group flex flex-col justify-end h-full">
                                                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 text-[10px] font-bold text-salon-primary opacity-0 group-hover:opacity-100 transition-opacity bg-salon-surface border border-salon-golden/30 px-2 py-1 rounded shadow-xl z-20 whitespace-nowrap">
                                                            ${Number(day.total).toFixed(2)}
                                                        </div>
                                                        <motion.div 
                                                            initial={{ height: 0 }}
                                                            animate={{ height: `${pct}%` }}
                                                            className="w-full bg-salon-golden transition-all rounded-t-sm cursor-pointer relative group-hover:bg-salon-golden/80 group-hover:outline group-hover:outline-1 group-hover:outline-dotted group-hover:outline-salon-golden group-hover:outline-offset-4 shadow-[0_0_10px_rgba(197,160,89,0.2)]"
                                                        ></motion.div>
                                                        <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[9px] text-salon-muted uppercase tracking-tighter whitespace-nowrap rotate-45 origin-left">
                                                            {new Date(day.day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className="bg-salon-surface/30 border border-salon-golden/10 rounded-lg p-8">
                                        <h3 className="font-serif text-xl text-salon-primary mb-6 flex items-center gap-2">
                                            <Star className="w-4 h-4 text-salon-golden" /> Elite Coupons
                                        </h3>
                                        <div className="space-y-6">
                                            {business?.topCoupons.map((coupon, idx) => (
                                                <div key={idx} className="flex flex-col gap-2 border-b border-salon-golden/5 pb-4 last:border-0">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-xs font-bold tracking-widest text-salon-golden uppercase">{coupon.code}</span>
                                                        <span className="text-[10px] text-salon-muted">{coupon.usage_count} Uses</span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-[10px] text-salon-muted uppercase tracking-widest">Total Value Given</span>
                                                        <span className="text-sm font-serif text-salon-primary">${Number(coupon.total_discount).toFixed(2)}</span>
                                                    </div>
                                                    <div className="w-full h-1 bg-salon-base rounded-full overflow-hidden">
                                                        <div 
                                                            className="h-full bg-salon-golden" 
                                                            style={{ width: `${(coupon.usage_count / (business?.topCoupons[0].usage_count || 1)) * 100}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                            {business?.topCoupons.length === 0 && (
                                                <div className="text-center py-10 text-salon-muted italic text-sm">No coupon data for this period.</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </motion.div>
                ) : (
                    <motion.div 
                        key="staff"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="space-y-8"
                    >
                        {isStaffLoading ? (
                            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-salon-golden" /></div>
                        ) : (
                            <>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                    {staff.map((s, idx) => (
                                        <button
                                            key={idx}
                                            type="button"
                                            onClick={() => navigate(`/admin/reports/staff/${s.id}`)}
                                            className="group relative bg-salon-surface/40 border border-salon-golden/10 rounded-xl p-8 transition-all hover:border-salon-golden/30 hover:shadow-xl hover:shadow-salon-golden/5 text-left overflow-hidden"
                                        >
                                            <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-all transform translate-x-2 group-hover:translate-x-0">
                                                <ChevronRight className="w-5 h-5 text-salon-golden" />
                                            </div>

                                            <div className="flex items-center gap-4 mb-8">
                                                <div className="w-16 h-16 rounded-full bg-salon-golden/10 border border-salon-golden/20 flex items-center justify-center transition-transform group-hover:scale-110 duration-500">
                                                    {s.avatar_url ? (
                                                        <img src={s.avatar_url} alt={s.name} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <UserCheck className="w-8 h-8 text-salon-golden" />
                                                    )}
                                                </div>
                                                <div>
                                                    <h4 className="text-xl font-serif text-salon-primary group-hover:text-salon-golden transition-colors">{s.name}</h4>
                                                    <p className="text-[10px] uppercase tracking-[0.2em] text-salon-muted font-bold">{s.role}</p>
                                                </div>
                                            </div>

                                            <div className="space-y-6">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-[10px] uppercase tracking-widest text-salon-muted font-bold">Revenue Generated</span>
                                                    <span className="text-2xl font-serif text-salon-golden">${Number(s.revenue || 0).toLocaleString()}</span>
                                                </div>

                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="bg-salon-base/40 p-4 rounded-lg border border-salon-golden/5">
                                                        <div className="text-[9px] uppercase tracking-widest text-salon-muted mb-1 font-bold">Rituals</div>
                                                        <div className="text-xl font-serif text-salon-primary">{s.total_bookings}</div>
                                                    </div>
                                                    <div className="bg-salon-base/40 p-4 rounded-lg border border-salon-golden/5">
                                                        <div className="text-[9px] uppercase tracking-widest text-salon-muted mb-1 font-bold">Retention</div>
                                                        <div className="text-xl font-serif text-salon-primary">
                                                            {s.total_bookings > 0
                                                                ? (100 - (s.cancellations / (Number(s.total_bookings) + Number(s.cancellations)) * 100)).toFixed(0)
                                                                : 100}%
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </>
                        )
                    }
                        </motion.div>
                    )
                }
                </AnimatePresence>
            </div>
    );
};

export default AdminReports;
